import { useState } from 'react';

import {
  AdminApiError,
  deleteAccount,
  previewDeleteAccount,
  type DeletePreview,
} from '@/lib/paymentsApi';

import { MIN_REASON_LENGTH } from './ResetPasswordDialog';

interface DeleteAccountDialogProps {
  /** The profile id — the same id `/users/:userId` and `/users/:id/payments`
   * both address; the delete routes take it directly. */
  userId: string;
  /** The address the card already loaded, shown before the preview so the
   * agent can see WHOSE account this is without a round-trip. The confirm
   * wall still checks against the address the SERVER returns in the preview,
   * never this one. */
  email?: string | null;
  onClose: () => void;
  onSuccess: (outcome: string) => void;
}

/**
 * THE ONE IMPLEMENTATION OF ACCOUNT DELETION IN THIS APP (ClickUp 869f5q8kd,
 * 22.09.2026). It was an inline section on `/users/:id/payments` — a route
 * nothing on the user's card links to, reachable only by pasting a raw
 * profile id into the Payments lookup. That is why the support lead could see
 * Transfer and not Delete: not a permission, not a stale bundle, a door with
 * no handle on the page he was on.
 *
 * Extracted rather than copied. A second inline copy of an IRREVERSIBLE flow
 * is how one of the two ends up with a weaker wall than the other after a
 * refactor nobody re-read; both entry points now open this.
 *
 * THE RITUAL IS UNCHANGED, and deliberately the same one TransferAccountDialog
 * uses — the two doors that end a login ask for the same three things in the
 * same order:
 *   1. a REASON, mandatory, at least MIN_REASON_LENGTH characters. Untouched
 *      by the 22.09 grant relaxation: that was credits, this is erasure.
 *   2. a PREVIEW, its own step and its own request — what will be erased,
 *      which organizations go with it, whether a sole-admin membership blocks
 *      it. The delete button does not exist until the server has answered.
 *   3. a CONFIRM WALL: the account's own email, typed by hand, matched
 *      case-insensitively against the address the PREVIEW returned.
 * The API separately checks the role, a non-empty reason, the current email
 * and sole-admin restrictions before starting audited erasure. The preview
 * ordering and ten-character reason floor above are client-side safeguards.
 */
export function DeleteAccountDialog({ userId, email, onClose, onSuccess }: DeleteAccountDialogProps) {
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [preview, setPreview] = useState<DeletePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedReason = reason.trim();
  const canPreview = trimmedReason.length >= MIN_REASON_LENGTH && !busy;
  // Matched against the PREVIEW's address, not the prop: the card may be
  // stale, and the thing being erased is whatever the server just described.
  const confirmTarget = preview?.email ?? null;
  const confirmMatches =
    Boolean(confirmTarget) && confirm.trim().toLowerCase() === (confirmTarget ?? '').toLowerCase();
  const canDelete =
    preview !== null && preview.blocked_by_sole_admin_of_org === null && confirmMatches && !busy;

  async function handlePreview() {
    if (!canPreview) return;
    setBusy(true);
    setError(null);
    try {
      setPreview(await previewDeleteAccount(userId, trimmedReason));
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'Could not preview the deletion.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!canDelete) return;
    setBusy(true);
    setError(null);
    try {
      const result = await deleteAccount(userId, trimmedReason, confirm.trim());
      onSuccess(result.outcome);
      onClose();
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'Could not delete the account.');
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete account"
      className="fixed inset-0 z-popover flex items-center justify-center bg-scrim/40 px-4"
    >
      <div className="max-h-screen w-full max-w-lg overflow-y-auto rounded-panel border border-destructive-border bg-surface-field p-6">
        <h2 className="text-h3 font-semibold text-destructive-soft-foreground">Delete account</h2>
        <p className="mt-2 text-body text-fg-secondary">
          Irreversible. This erases the account and everything it owns that no one else does.
          Preview what will go before confirming.
        </p>

        <label className="mt-4 block text-meta font-medium text-fg-secondary" htmlFor="delete-reason">
          Reason (required, at least {MIN_REASON_LENGTH} characters)
        </label>
        <textarea
          id="delete-reason"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            // A changed reason invalidates the preview it was taken under —
            // the reason is written to the audit row, so the sentence that
            // justified the preview must be the sentence that ships.
            setPreview(null);
          }}
          rows={3}
          disabled={busy}
          className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary focus-visible:border-border-focus"
        />

        {preview === null && email ? (
          <p className="mt-3 text-meta text-fg-tertiary">
            This card: <span className="font-mono text-micro">{email}</span>
          </p>
        ) : null}

        {preview ? <PreviewPanel preview={preview} /> : null}

        {preview ? (
          <>
            <label
              className="mt-4 block text-meta font-medium text-fg-secondary"
              htmlFor="delete-confirm"
            >
              Type the account&apos;s email to confirm ({confirmTarget ?? 'unknown'})
            </label>
            <input
              id="delete-confirm"
              type="text"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              disabled={busy}
              autoComplete="off"
              className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary focus-visible:border-border-focus"
            />
          </>
        ) : null}

        {error ? (
          <p
            role="alert"
            className="mt-3 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-card px-4 py-2 text-body font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          {preview === null ? (
            <button
              type="button"
              onClick={handlePreview}
              disabled={!canPreview}
              className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Loading…' : 'Preview what will be erased'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDelete}
              disabled={!canDelete}
              className="rounded-card bg-destructive px-4 py-2 text-body font-medium text-destructive-foreground hover:bg-destructive-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Delete account permanently'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewPanel({ preview }: { preview: DeletePreview }) {
  return (
    <div
      className="mt-4 rounded-card border border-border bg-surface-muted p-3 text-body"
      data-testid="delete-preview"
    >
      <div className="text-meta font-medium text-fg-secondary">What will be erased</div>
      <dl className="mt-2 grid grid-cols-2 gap-y-1 text-meta text-fg-secondary">
        <dt>Email</dt>
        <dd className="text-fg-primary">{preview.email ?? '—'}</dd>
        <dt>Organizations to be deleted</dt>
        <dd className="text-fg-primary">
          {preview.organizations_to_be_deleted.length > 0
            ? preview.organizations_to_be_deleted.join(', ')
            : 'none'}
        </dd>
        <dt>Has payment history</dt>
        <dd className="text-fg-primary">{preview.has_payment_history ? 'yes' : 'no'}</dd>
        <dt>Payment tokens via</dt>
        <dd className="text-fg-primary">
          {preview.payment_tokens_will_be_requested_deleted_via ?? '—'}
        </dd>
        <dt>Third-party targets</dt>
        <dd className="text-fg-primary">
          {preview.third_party_targets.length > 0 ? preview.third_party_targets.join(', ') : 'none'}
        </dd>
      </dl>

      {preview.blocked_by_sole_admin_of_org ? (
        <p
          role="alert"
          className="mt-3 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
        >
          Blocked: this account is the only admin of organization{' '}
          {preview.blocked_by_sole_admin_of_org}, which has other members. Give that organization
          another admin first.
        </p>
      ) : null}
    </div>
  );
}
